// App-wide state (Preact signals) and the data-loading actions that feed the 3D viewer.
import { signal } from '@preact/signals';
import { get } from './api.js';
import { setPoiCategories } from './viewer/style.js';

export const me = signal(null);
// roles and feature switches (the server enforces both; these just decide what to show)
export const isGlobal = () => me.value?.role === 'global';
export const isAdmin = () => ['admin', 'global'].includes(me.value?.role);
export const canWrite = () => ['contributor', 'admin', 'global'].includes(me.value?.role);
export const feat = (k) => me.value?.features?.[k] !== false;
export const model = signal(null);
export const modelStatus = signal(null);
export const places = signal([]);
export const caves = signal([]);
export const basemaps = signal([]);
export const basemap = signal('hillshade');
function stored(k, d) { try { const v = localStorage.getItem(k); return v == null ? d : JSON.parse(v); } catch { return d; } }
function remember(k, v) { try { localStorage.setItem(k, JSON.stringify(v)); } catch { /* private mode */ } }

// which name labels are on (remembered per browser)
const LABELS = { caves: true, places: true, notes: true, passages: true, parcels: false, hydro: true, features: true, records: true, observations: false, studies: false, leads: false, names: true, stations: false, stationsAll: false, cove: true };
function storedLabels() { try { return { ...LABELS, ...JSON.parse(localStorage.getItem('atlas.labels') || '{}') }; } catch { return { ...LABELS }; } }
export function setLabels(l) {
  const labels = { ...opts.value.labels, ...l };
  setOpts({ labels });
  try { localStorage.setItem('atlas.labels', JSON.stringify(labels)); } catch { /* private mode */ }
}
export const placeNames = signal(null);
export const sinks = signal(null);            // {sinks, built, running, last} from /api/sinks
export const sinkAnalysis = signal(null);     // {doc, running, last, legs_ext} from /api/sinks/analysis
export const sinkFilter = signal({ color: stored('atlas.sinks.color', 'depth'), prospects: false, minDepth: 0.5, minDiam: 0, maxDiam: 60, open: true, nested: true, flagged: false,
  rec: '', near: 0, sort: 'depth', show: stored('atlas.sinks.show', null) });
export const parcels = signal(null);          // {parcels, built, tags, owners_visible, ...} from /api/parcels
export const hydro = signal(null);            // {basins, springs, flowpaths, source, citation, accuracy, custom} from /api/hydrology
export const water = signal(null);            // {routes, links, can_write} from /api/water (superseded links only when asked for)
export const waterAll = signal(null);         // the same with every link
export const cove = signal(null);             // {lines, entrances, stations, entrances_restricted, installed} from /api/cove-caves
export const channels = signal(null);         // {reaches, sinks, maps, built} or {building} from /api/water/channels
export const measure = signal({ on: false, pts: [] });
// the profile along a drawn line: {open, drawing, doc (the profile being looked at), saved (JSON of it as last saved)}
export const profileUI = signal({ open: false, drawing: false, doc: null, saved: null });       // {names, source, running, last} from /api/names

export const opts = signal({ opacity: 0.45, xray: true, contours: false, depressions: true, mapMode: 'cave', mapOpacity: 0.85,
  ve: 1.5, shift: 0, colorBy: 'depth', walls: true, wallOpacity: 1, centerline: true, parcels: stored('atlas.parcels', false),
  hydro: stored('atlas.hydro', false), hydroFill: stored('atlas.hydroFill', 0.3),
  waterChannels: stored('atlas.water.channels', false), waterRoutes: stored('atlas.water.routes', false),
  waterLinks: stored('atlas.water.links', false), waterNeg: stored('atlas.water.neg', false),
  waterSuperseded: stored('atlas.water.superseded', false), hydroDivides: stored('atlas.hydro.divides', false),
  coveLines: stored('atlas.cove.lines', false), coveEntrances: stored('atlas.cove.entrances', false),
  coveSprings: stored('atlas.cove.springs', false), coveStations: stored('atlas.cove.stations', false),
  layers: { feature: true, observation: true, study_point: true, record: stored('atlas.layer.record', true) },
  poiHidden: stored('atlas.poi.hidden', []),
  xsPoints: stored('atlas.xsPoints', false),            // drawn cross-sections as small squares on the survey line           // POI categories switched off (0 = no category)
  labels: storedLabels() });
export const filter = signal({ caves: null, surveys: null, yearMin: null, yearMax: null, mode: 'dim' });
export const selection = signal(null);
export const modalStack = signal([]);
export const picking = signal(null);
export const toast = signal(null);
export const tab = signal((() => { try { return localStorage.getItem('atlas.tab') || 'ground'; } catch { return 'ground'; } })());
tab.subscribe((v) => { try { localStorage.setItem('atlas.tab', v); } catch { /* private mode */ } });
export const studyFocus = signal(null);
export const dataVersion = signal(0);         // bump to make lists refetch
export const geology = signal(null);          // 3D geology model meta (column, sections, sources) or null
export const geoView = signal({ sections: [], surfaces: [], surfOpacity: 0.7 });

// Display quality: how many pixels to draw and how much terrain detail to load (remembered per browser).
// sse = the largest a terrain grid cell may appear on screen, in pixels, before finer tiles are loaded.
export const QUALITY = {
  fast: { label: 'Fast', pixelRatio: 1, sse: 22 },
  balanced: { label: 'Balanced', pixelRatio: 1.5, sse: 13 },
  sharp: { label: 'Sharp', pixelRatio: 2, sse: 7 },
};
function storedQuality() { try { return localStorage.getItem('atlas.quality') || 'balanced'; } catch { return 'balanced'; } }
export const quality = signal(QUALITY[storedQuality()] ? storedQuality() : 'balanced');

export function setQuality(q) {
  quality.value = q;
  try { localStorage.setItem('atlas.quality', q); } catch { /* private mode */ }
  viewer.setQuality(QUALITY[q]);
}

export let viewer = null;
export function setViewer(v) { viewer = v; }

export function setOpts(o) { opts.value = { ...opts.value, ...o }; viewer?.setOptions(o); }
export function setFilter(f) { filter.value = { ...filter.value, ...f }; viewer?.setFilter(filter.value); }

export async function loadPlaces() {
  const [P] = await Promise.all([get('/api/places'), feat('records') ? loadPoiCategories(false) : null]);
  places.value = P;
  viewer?.setPlaces(places.value);
}

// ------------------------------------------------------------------ survey data and cross-section points
export const surveyData = signal(null);       // the data panel: {fileId, k (survey in the file), station, shot} or null
export function openSurveyData(o) { surveyData.value = { ...(surveyData.value || {}), ...o, t: Date.now() }; }
export function showXsPoints(on) { setOpts({ xsPoints: on }); remember('atlas.xsPoints', on); if (on) loadXsPoints(); }
export async function loadXsPoints() {
  try { viewer?.setXsPoints(await get('/api/xsections')); } catch (e) { console.warn('cross-sections not loaded', e); }
}

// ------------------------------------------------------------------ points of interest
export const poiCategories = signal([]);        // [{id, name, color, shape, icon, count, can_edit}]
export async function loadPoiCategories(redraw = true) {
  try { poiCategories.value = await get('/api/records/categories'); } catch { poiCategories.value = []; }
  setPoiCategories(poiCategories.value);
  if (redraw && viewer) viewer.setPlaces(places.value);
  return poiCategories.value;
}
export function showPoi(on) {
  setOpts({ layers: { ...opts.value.layers, record: on } }); remember('atlas.layer.record', on);
}
export function setPoiHidden(catId, hidden) {
  const k = catId ?? 0, cur = opts.value.poiHidden || [];
  const v = hidden ? [...new Set([...cur, k])] : cur.filter(x => x !== k);
  setOpts({ poiHidden: v }); remember('atlas.poi.hidden', v);
}

export async function loadCaves() { caves.value = await get('/api/caves'); syncCaveNames(); return caves.value; }

// cave names in the 3D model are fixed when it is built; a renamed cave takes its new name straight away
export function syncCaveNames() {
  const M = model.value; if (!M || !caves.value.length) return;
  let changed = false;
  for (const c of M.caves) { const x = caves.value.find(y => y.id === c.id); if (x && x.name !== c.name) { c.name = x.name; changed = true; } }
  if (changed) { model.value = { ...M }; viewer?.labels.setCaves(); }
}

// ------------------------------------------------------------------ passage colours
// The club default (an admin saves it) with this member's own changes on top (remembered in this browser).
// Keys: depth / elevation / year {stops: [[value, '#hex']]}, above, bands [{mode, from, to, color, label, on}],
// outside ('ramp' | 'grey'), caves {id: '#hex'}, units {code: '#hex'}.  A key set here replaces the club's.
export const clubColors = signal({});
export const myColors = signal(stored('atlas.colors', {}));
export const colorScheme = () => ({ ...clubColors.value, ...myColors.value });
export function setMyColors(patch) {
  const v = { ...myColors.value, ...patch };
  for (const k of Object.keys(patch)) if (patch[k] === undefined) delete v[k];
  myColors.value = v; remember('atlas.colors', v);
  viewer?.setScheme(colorScheme());
}
export async function loadClubColors() {
  try { clubColors.value = (await get('/api/display/colors')) || {}; } catch { clubColors.value = {}; }
  viewer?.setScheme(colorScheme());
}

// ------------------------------------------------------------------ named places in caves
export const NOTE_KINDS = new Set(['note', 'floor', 'height', 'drop', 'width', 'elevation', 'measure']);
export const cavePlaces = signal([]);
const NOTE_SIGN = { height: '↕', drop: '⇣', width: '↔', elevation: '△' };
export function placeLabel(p) {
  if (!NOTE_KINDS.has(p.kind)) return p.name;
  return p.value != null ? `${NOTE_SIGN[p.kind] || ''}${NOTE_SIGN[p.kind] ? ' ' : ''}${+p.value.toFixed(1)} ${p.unit || ''}`.trim() : p.name;
}
export async function loadCavePlaces() {
  try {
    const L = await get('/api/cave-places');
    cavePlaces.value = L;
    viewer?.setCavePlaces(L.map(p => ({ ...p, note: NOTE_KINDS.has(p.kind), label: placeLabel(p),
      norm: p.name.toLowerCase().replace(/['’]s\b/g, 's').replace(/[^a-z0-9]+/g, ' ').trim().replace(/^the /, '') })));
    return L;
  } catch (e) { console.warn('places not loaded', e); return []; }
}

// ------------------------------------------------------------------ section slicer
// {on, x: [e0, e1] | null, y: [n0, n1] | null, z: [z0, z1] | null, terrain, box}; easting / northing / elevation in metres
export const slicer = signal({ on: false, x: null, y: null, z: null, terrain: true, box: true });
export function setSlicer(p) {
  slicer.value = { ...slicer.value, ...p };
  const c = slicer.value;
  viewer?.setClip(c.on ? c : null);
}

export async function loadBasemaps() {
  const all = await get('/api/basemaps');
  basemaps.value = all.filter(b => !b.overlay);
  const dep = all.find(b => b.key === 'depressions');
  if (dep) viewer?.setDepressionLayer(dep);
  const cur = basemaps.value.find(b => b.key === basemap.value);
  if (cur?.ready) viewer?.setBasemap(cur);           // picks up a re-downloaded version
}

export function chooseBasemap(key) {
  const b = basemaps.value.find(x => x.key === key);
  if (!b || !b.ready) return;
  basemap.value = key;
  viewer.setBasemap(b);
}

export async function loadModel() {
  try {
    const m = await get('/api/model');
    model.value = m;
    if (caves.value.length) syncCaveNames();
    await viewer.loadModel(m);
    if (water.value) viewer.setWater(water.value);          // streams in caves follow the stations
    if (m.mapsheet) await viewer.setMapsheetTexture(`/api/assets/mapsheet?v=${m.mapsheet.version}`);
    viewer.setPlaces(places.value);
    if (opts.value.xsPoints && me.value?.features?.xsections !== false) loadXsPoints();     // they follow the stations
  } catch (e) {
    model.value = null;
    if (e.status !== 404) throw e;
  }
}

export async function loadGeology() {
  try {
    const m = await get('/api/geology');
    const r = await fetch(`/api/geology/${m.version}/field.bin`, { credentials: 'same-origin' });
    if (!r.ok) throw new Error('geology field ' + r.status);
    const { GeoModel } = await import('./viewer/geology.js');
    viewer.setGeology(new GeoModel(m, await r.arrayBuffer()));
    geology.value = m; geoView.value = { sections: [], surfaces: [], surfOpacity: 0.7 };
  } catch (e) {
    geology.value = null;
    if (e.status !== 404) console.warn('geology model not loaded', e);
  }
}

export async function loadNames() {
  try {
    const r = await get('/api/names');
    placeNames.value = r;
    viewer?.setNames(r.names || []);
    return r;
  } catch (e) { console.warn('place names not loaded', e); return null; }
}

export async function loadSinks() {
  try { sinks.value = await get('/api/sinks'); applySinkFilter(); loadSinkAnalysis(); return sinks.value; }
  catch (e) { console.warn('sinks not loaded', e); return null; }
}

// the sinkhole-finder results that pass the filter, sorted; also what the map shows
export async function loadSinkAnalysis() {
  try { sinkAnalysis.value = await get('/api/sinks/analysis'); applySinkFilter(); return sinkAnalysis.value; }
  catch (e) { console.warn('sink analysis not loaded', e); return null; }
}
// what the analysis says about one sink: {passage_m, passage_depth_m, over_stream, cls (0 over, 1 near, 2 far, 3 suspect), likeness}
export function sinkVerdict(id) {
  const r = sinkAnalysis.value?.doc?.sinks?.[id];
  return r ? { passage_m: r[0], passage_depth_m: r[1], over_stream: !!r[2], cls: r[3], likeness: r[4] } : null;
}
export const SINK_CLASS = [{ label: 'Over surveyed passage', color: '#d95926' }, { label: 'Near it', color: '#199e70' },
  { label: 'Away from known passage', color: '#3987e5' }, { label: 'Probably not a sinkhole', color: '#8c8b82' }];
const LIKE_RAMP = ['#184f95', '#256abf', '#3987e5', '#6da7ec', '#9ec5f4', '#cde2fb'];      // sequential blue, dark (low) to light
export const likenessColor = (v) => LIKE_RAMP[Math.max(0, Math.min(LIKE_RAMP.length - 1, Math.floor(v * LIKE_RAMP.length)))];
export function sinkColor(s) {
  const mode = sinkFilter.value.color, v = mode !== 'depth' && sinkVerdict(s.id);
  if (!v) return null;
  if (mode === 'passage') return SINK_CLASS[v.cls]?.color || null;
  if (mode === 'likeness') return v.likeness == null ? '#5b5a55' : likenessColor(v.likeness);
  return null;
}
export function filteredSinks() {
  const f = sinkFilter.value, L0 = sinks.value?.sinks || [];
  const P = f.prospects && sinkAnalysis.value?.doc?.prospects ? new Set(sinkAnalysis.value.doc.prospects) : null;
  const L = P ? L0.filter(s => P.has(s.id)) : L0;
  const out = L.filter(s => s.depth >= f.minDepth && s.diameter >= f.minDiam && (!f.maxDiam || s.diameter <= f.maxDiam)
    && (f.open || !s.flags.includes('open')) && (f.nested || !s.flags.includes('nested'))
    && (f.flagged || !(s.flags.includes('flat floor') || s.flags.includes('elongated')))
    && (!f.rec || (f.rec === 'no' ? !s.recorded : f.rec === 'yes' ? !!s.recorded : f.rec === 'dig' ? s.recorded?.kind === 'dig' : s.recorded?.kind !== 'dig')) && (!f.near || (s.passage_m != null && s.passage_m <= f.near)));
  const key = { depth: (s) => -s.depth, diameter: (s) => -s.diameter, steep: (s) => -(s.depth / Math.max(s.diameter, 1)),
    passage: (s) => s.passage_m ?? 1e9, likeness: (s) => -(sinkVerdict(s.id)?.likeness ?? -1) }[f.sort] || ((s) => -s.depth);
  return out.sort((a, b) => key(a) - key(b));
}
export function applySinkFilter() { viewer?.setSinks(sinkFilter.value.show ? filteredSinks() : [], sinkFilter.value.color === 'depth' ? null : sinkColor); }
export function setSinkFilter(f) {
  sinkFilter.value = { ...sinkFilter.value, ...f };
  if ('color' in f) remember('atlas.sinks.color', f.color);
  if ('show' in f) { remember('atlas.sinks.show', !!f.show); if (f.show && !sinks.value) { loadSinks(); return; } }
  applySinkFilter();
}
export function showParcels(on) {
  setOpts({ parcels: on }); remember('atlas.parcels', on);
  if (on && !parcels.value) loadParcels();
}

export function showHydro(on) {
  setOpts({ hydro: on }); remember('atlas.hydro', on);
  if (on && !hydro.value) loadHydro();
}
export function setHydroFill(v) { setOpts({ hydroFill: v }); remember('atlas.hydroFill', v); }
export function showHydroDivides(on) {          // the Cove book's basin divides, painted with the basins
  setOpts({ hydroDivides: on }); remember('atlas.hydro.divides', on);
  if (hydro.value) viewer?.setHydro(hydro.value);
}

export async function loadHydro() {
  try { hydro.value = await get('/api/hydrology'); viewer?.setHydro(hydro.value); return hydro.value; }
  catch (e) { console.warn('hydrology not loaded', e); return null; }
}

// ------------------------------------------------------------------ the cove caves overlay (GeoPackage)
// part: 'coveLines' | 'coveEntrances' | 'coveSprings' | 'coveStations'
export const COVE_PARTS = ['coveLines', 'coveEntrances', 'coveSprings', 'coveStations'];
export function showCove(part, on) {
  setOpts({ [part]: on }); remember('atlas.cove.' + part.slice(4).toLowerCase(), on);
  if (on && !cove.value) loadCove();
}
export async function loadCove() {
  try { cove.value = await get('/api/cove-caves'); viewer?.setCove(cove.value); return cove.value; }
  catch (e) { console.warn('cove caves not loaded', e); return null; }
}

// ------------------------------------------------------------------ the Water layer
// part: 'channels' | 'routes' | 'links' | 'neg' | 'superseded'
const WATER_OPT = { channels: 'waterChannels', routes: 'waterRoutes', links: 'waterLinks', neg: 'waterNeg', superseded: 'waterSuperseded' };
export function showWater(part, on) {
  const k = WATER_OPT[part]; setOpts({ [k]: on }); remember('atlas.water.' + part, on);
  if (on && part === 'channels' && !channels.value?.reaches?.length) loadChannels();
  if (on && part !== 'channels' && !water.value) loadWater();
  if (part === 'superseded' && waterAll.value) applyWater();
}
function applyWater() {            // superseded connections (replaced by a later trace or a merged record) are left out
  const W = waterAll.value;
  water.value = opts.value.waterSuperseded ? W : { ...W, links: W.links.filter(l => l.status !== 'superseded') };
  viewer?.setWater(water.value);
}
export async function loadWater() {
  try { waterAll.value = await get('/api/water'); applyWater(); return water.value; }
  catch (e) { console.warn('water not loaded', e); return null; }
}
let chTimer = null;
export async function loadChannels() {
  try {
    const d = await get('/api/water/channels');
    channels.value = d;
    if (d.building) { clearTimeout(chTimer); chTimer = setTimeout(loadChannels, 8000); return d; }      // worked out the first time: about a minute
    viewer?.setChannels(d); return d;
  } catch (e) { console.warn('channels not loaded', e); return null; }
}

function inRings(rings, E, N) {
  let inside = false;
  for (const r of rings) for (let i = 0, j = r.length - 1; i < r.length; j = i++) {
    const [xi, yi] = r[i], [xj, yj] = r[j];
    if ((yi > N) !== (yj > N) && E < (xj - xi) * (N - yi) / (yj - yi) + xi) inside = !inside;
  }
  return inside;
}
// the drainage basin a point lies in, or null
export function basinAt(E, N) {
  for (const b of hydro.value?.basins || []) {
    const bb = b._bb ||= b.rings.flat().reduce((q, v) => [Math.min(q[0], v[0]), Math.min(q[1], v[1]), Math.max(q[2], v[0]), Math.max(q[3], v[1])], [1e12, 1e12, -1e12, -1e12]);
    if (E < bb[0] || E > bb[2] || N < bb[1] || N > bb[3]) continue;
    if (inRings(b.rings, E, N)) return b;
  }
  return null;
}
export const inBasin = (b, E, N) => inRings(b.rings, E, N);

export async function loadParcels() {
  try { parcels.value = await get('/api/parcels'); viewer?.setParcels(parcels.value.parcels); return parcels.value; }
  catch (e) { console.warn('parcels not loaded', e); return null; }
}

// the parcel a ground point falls in (even-odd rule over its rings), or null
export function parcelAt(E, N) {
  for (const p of parcels.value?.parcels || []) {
    const bb = p._bb ||= p.rings.flat().reduce((b, v) => [Math.min(b[0], v[0]), Math.min(b[1], v[1]), Math.max(b[2], v[0]), Math.max(b[3], v[1])], [1e12, 1e12, -1e12, -1e12]);
    if (E < bb[0] || E > bb[2] || N < bb[1] || N > bb[3]) continue;
    let inside = false;
    for (const r of p.rings) for (let i = 0, j = r.length - 1; i < r.length; j = i++) {
      const [xi, yi] = r[i], [xj, yj] = r[j];
      if ((yi > N) !== (yj > N) && E < (xj - xi) * (N - yi) / (yj - yi) + xi) inside = !inside;
    }
    if (inside) return p;
  }
  return null;
}

export function refresh() { dataVersion.value++; return loadPlaces(); }

export async function pollModel() {
  const st = await get('/api/model/status');
  const prev = modelStatus.value;
  modelStatus.value = st;
  if (prev?.model && st.model && st.model.version !== prev.model.version) await loadModel();
  return st;
}
